import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSmartRefresh } from '../hooks/useSmartRefresh';
import { Building2, Users, Stethoscope, Calendar, LogOut, Plus, Edit2, Trash2, Upload, BarChart3, Clock, CheckCircle, Settings, User, Search, Shield, HeartPulse } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { PhoneInput } from './ui/phone-input';
import SecurityAuditTab from './SecurityAuditTab';
import axios from 'axios';
import { toast } from 'sonner';
import { formatLeaveDates } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Super Specialization options based on main specialization
const SUPER_SPECIALIZATION_OPTIONS = {
  'Pediatrician': ['Neonatology', 'Pediatric Cardiology', 'Pediatric Neurology', 'Pediatric Oncology', 'Pediatric Pulmonology', 'Pediatric Gastroenterology', 'Pediatric Endocrinology', 'Pediatric Nephrology', 'Pediatric Rheumatology', 'Developmental Pediatrics'],
  'General Medicine': ['Internal Medicine', 'Critical Care Medicine', 'Infectious Disease', 'Geriatric Medicine', 'Palliative Medicine'],
  'General Surgeon': ['Laparoscopic Surgery', 'Trauma Surgery', 'Surgical Oncology', 'Vascular Surgery', 'Colorectal Surgery'],
  'Neurosurgeon': ['Spine Surgery', 'Pediatric Neurosurgery', 'Neuro-oncology', 'Functional Neurosurgery', 'Cerebrovascular Surgery'],
  'Cardiologist': ['Interventional Cardiology', 'Electrophysiology', 'Heart Failure', 'Preventive Cardiology', 'Cardiac Imaging'],
  'Neurologist': ['Epilepsy', 'Movement Disorders', 'Stroke', 'Neuromuscular Disease', 'Headache Medicine', 'Neuro-immunology'],
  'Pulmonologist': ['Critical Care', 'Sleep Medicine', 'Interventional Pulmonology', 'Pulmonary Hypertension'],
  'Gastroenterologist': ['Hepatology', 'IBD', 'Pancreatic Disease', 'GI Oncology', 'Motility Disorders'],
  'Endocrinologist': ['Diabetes', 'Thyroid Disorders', 'Reproductive Endocrinology', 'Bone & Mineral Disorders'],
  'Dermatologist': ['Cosmetic Dermatology', 'Pediatric Dermatology', 'Dermatopathology', 'Mohs Surgery'],
  'ENT Specialist': ['Otology', 'Rhinology', 'Laryngology', 'Head & Neck Surgery', 'Pediatric ENT'],
  'Dentist': ['Orthodontics', 'Periodontics', 'Endodontics', 'Prosthodontics', 'Oral Surgery', 'Pediatric Dentistry'],
  'Psychiatrist': ['Child & Adolescent Psychiatry', 'Addiction Psychiatry', 'Geriatric Psychiatry', 'Forensic Psychiatry'],
  'Obstetrician/Gynec': ['Maternal-Fetal Medicine', 'Reproductive Endocrinology', 'Gynecologic Oncology', 'Urogynecology'],
  'Oncologist': ['Medical Oncology', 'Radiation Oncology', 'Surgical Oncology', 'Hematology-Oncology', 'Pediatric Oncology'],
  'Urologist': ['Pediatric Urology', 'Urologic Oncology', 'Female Urology', 'Andrology', 'Renal Transplant'],
  'Physiotherapy': ['Sports Physiotherapy', 'Neurological Physiotherapy', 'Pediatric Physiotherapy', 'Cardiopulmonary Physiotherapy', 'Orthopedic Physiotherapy']
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [activeTab, setActiveTab] = useState('hospital');
  
  // Hospital state
  const [hospitals, setHospitals] = useState([]);
  const [showHospitalDialog, setShowHospitalDialog] = useState(false);
  const [editingHospital, setEditingHospital] = useState(null);
  const [hospitalForm, setHospitalForm] = useState({ 
    name: '', 
    address_line1: '', 
    address_line2: '', 
    city: '', 
    state: '', 
    pincode: '',
    contact: '',
    country_code: '+91',
    logo_url: ''
  });
  const [selectedHospitalLogo, setSelectedHospitalLogo] = useState(null);
  
  // Front Desk state
  const [frontDeskStaff, setFrontDeskStaff] = useState([]);
  const [showStaffDialog, setShowStaffDialog] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffForm, setStaffForm] = useState({ name: '', email: '', phone: '', password: '', hospital_id: '', country_code: '+91' });
  
  // Medical Assistant state
  const [medicalAssistants, setMedicalAssistants] = useState([]);
  const [showMADialog, setShowMADialog] = useState(false);
  const [editingMA, setEditingMA] = useState(null);
  const [maForm, setMAForm] = useState({ name: '', email: '', phone: '', password: '', hospital_id: '', doctor_ids: [], country_code: '+91' });
  
  // Doctor state
  const [doctors, setDoctors] = useState([]);
  const [showDoctorDialog, setShowDoctorDialog] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [doctorForm, setDoctorForm] = useState({ 
    name: '', 
    specialization: '', 
    super_specialization: '',
    contact: '', 
    password: '',
    photo_url: '', 
    schedule: '',
    registered_number: '',
    country_code: '+91'
  });
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [searchedDoctor, setSearchedDoctor] = useState(null); // Stores found doctor from search
  const [searchingDoctor, setSearchingDoctor] = useState(false); // Loading state for search
  const [isExistingDoctor, setIsExistingDoctor] = useState(false); // Flag if doctor exists
  
  // Settings state
  const [showHospitalBrandingInPrint, setShowHospitalBrandingInPrint] = useState(true);
  const [showDoctorInfoInPrint, setShowDoctorInfoInPrint] = useState(true);
  const [showHospitalBrandingInPdf, setShowHospitalBrandingInPdf] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Indian states with abbreviations
  const indianStates = [
    { code: 'AP', name: 'Andhra Pradesh' },
    { code: 'AR', name: 'Arunachal Pradesh' },
    { code: 'AS', name: 'Assam' },
    { code: 'BR', name: 'Bihar' },
    { code: 'CG', name: 'Chhattisgarh' },
    { code: 'GA', name: 'Goa' },
    { code: 'GJ', name: 'Gujarat' },
    { code: 'HR', name: 'Haryana' },
    { code: 'HP', name: 'Himachal Pradesh' },
    { code: 'JH', name: 'Jharkhand' },
    { code: 'KA', name: 'Karnataka' },
    { code: 'KL', name: 'Kerala' },
    { code: 'MP', name: 'Madhya Pradesh' },
    { code: 'MH', name: 'Maharashtra' },
    { code: 'MN', name: 'Manipur' },
    { code: 'ML', name: 'Meghalaya' },
    { code: 'MZ', name: 'Mizoram' },
    { code: 'NL', name: 'Nagaland' },
    { code: 'OD', name: 'Odisha' },
    { code: 'PB', name: 'Punjab' },
    { code: 'RJ', name: 'Rajasthan' },
    { code: 'SK', name: 'Sikkim' },
    { code: 'TN', name: 'Tamil Nadu' },
    { code: 'TS', name: 'Telangana' },
    { code: 'TR', name: 'Tripura' },
    { code: 'UP', name: 'Uttar Pradesh' },
    { code: 'UK', name: 'Uttarakhand' },
    { code: 'WB', name: 'West Bengal' },
    { code: 'AN', name: 'Andaman and Nicobar Islands' },
    { code: 'CH', name: 'Chandigarh' },
    { code: 'DH', name: 'Dadra and Nagar Haveli and Daman and Diu' },
    { code: 'DL', name: 'Delhi' },
    { code: 'JK', name: 'Jammu and Kashmir' },
    { code: 'LA', name: 'Ladakh' },
    { code: 'LD', name: 'Lakshadweep' },
    { code: 'PY', name: 'Puducherry' }
  ];
  
  // Appointments state
  const [appointments, setAppointments] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    // Check if user is logged in
    const userData = localStorage.getItem('healthcareUser');
    if (!userData) {
      navigate('/healthcare-login');
      return;
    }
    
    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'admin') {
      toast.error('Access denied. Admin only.');
      navigate('/healthcare-login');
      return;
    }
    
    setUser(parsedUser);
    
    // Load hospital data from localStorage
    const hospitalData = localStorage.getItem('currentHospital');
    if (hospitalData) {
      setHospital(JSON.parse(hospitalData));
    }
    
    loadData();
  }, [navigate]);

  // Real-time data synchronization - Poll for updates every 30 seconds
  useEffect(() => {
    const syncData = async () => {
      // Check if user is actively working (dialog open, input focused, or typing)
      const activeElement = document.activeElement;
      const isInputActive = activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable
      );
      
      // Check if any dialog/modal is open
      const isDialogOpen = document.querySelector('[role="dialog"]') !== null;
      
      // Skip sync if user is actively working
      if (isInputActive || isDialogOpen) {
        console.log('⏸️ Skipping sync - user is actively working');
        return;
      }

      try {
        const currentHospitalData = localStorage.getItem('currentHospital');
        const currentHospital = currentHospitalData ? JSON.parse(currentHospitalData) : null;
        const currentHospitalId = currentHospital?.id;

        // Silently reload data in background
        const [hospitalsRes, staffRes, doctorsRes, appointmentsRes] = await Promise.all([
          axios.get(`${API}/hospitals`),
          axios.get(`${API}/front-desk-staff`),
          axios.get(`${API}/doctors`),
          axios.get(`${API}/appointments-detailed`)
        ]);
        
        // Filter data
        const filteredStaff = currentHospitalId
          ? staffRes.data.filter(s => s.hospital_id === currentHospitalId)
          : staffRes.data;
        
        const hospitalDoctors = currentHospitalId
          ? doctorsRes.data.filter(d => 
              d.hospital_id === currentHospitalId || 
              (d.hospital_ids && d.hospital_ids.includes(currentHospitalId))
            )
          : doctorsRes.data;
        
        const filteredAppointments = currentHospitalId
          ? appointmentsRes.data.filter(a => a.hospital_id === currentHospitalId)
          : appointmentsRes.data;
        
        // Only update if data has changed
        if (JSON.stringify(filteredStaff) !== JSON.stringify(frontDeskStaff)) {
          setFrontDeskStaff(filteredStaff);
          console.log('🔄 Front desk staff updated in real-time');
        }

        if (JSON.stringify(hospitalDoctors) !== JSON.stringify(doctors)) {
          setDoctors(hospitalDoctors);
          console.log('🔄 Doctors updated in real-time');
        }

        if (JSON.stringify(filteredAppointments) !== JSON.stringify(appointments)) {
          setAppointments(filteredAppointments);
          console.log('🔄 Appointments updated in real-time');
        }
      } catch (error) {
        console.error('Error syncing data:', error);
        // Silently fail - don't show error to user for background sync
      }
    };

    // Note: Smart auto-refresh is set up separately below
  }, [frontDeskStaff, doctors, appointments]);

  // Smart auto-refresh that pauses during user interactions
  useSmartRefresh(
    useCallback(() => {
      syncData();
    }, [frontDeskStaff, doctors, appointments]),
    30000, // 30 seconds
    3000   // Wait 3 seconds after user activity before resuming
  );

  const loadData = async () => {
    try {
      // Get current hospital from localStorage
      const currentHospitalData = localStorage.getItem('currentHospital');
      const currentHospital = currentHospitalData ? JSON.parse(currentHospitalData) : null;
      const currentHospitalId = currentHospital?.id;

      const [hospitalsRes, staffRes, doctorsRes, appointmentsRes, maRes] = await Promise.all([
        axios.get(`${API}/hospitals`),
        axios.get(`${API}/front-desk-staff`),
        axios.get(`${API}/doctors`),
        axios.get(`${API}/appointments-detailed`),
        axios.get(`${API}/medical-assistants`)
      ]);
      
      // Filter data by current hospital
      const filteredHospitals = currentHospitalId 
        ? hospitalsRes.data.filter(h => h.id === currentHospitalId)
        : hospitalsRes.data;
      
      const filteredStaff = currentHospitalId
        ? staffRes.data.filter(s => s.hospital_id === currentHospitalId)
        : staffRes.data;
      
      // Filter doctors by hospital - support both old (hospital_id) and new (hospital_ids array)
      const hospitalDoctors = currentHospitalId
        ? doctorsRes.data.filter(d => 
            d.hospital_id === currentHospitalId || 
            (d.hospital_ids && d.hospital_ids.includes(currentHospitalId))
          )
        : doctorsRes.data;
      
      // Deduplicate doctors by ID (keep first occurrence)
      const uniqueDoctors = [];
      const seenDoctorIds = new Set();
      for (const doctor of hospitalDoctors) {
        if (!seenDoctorIds.has(doctor.id)) {
          uniqueDoctors.push(doctor);
          seenDoctorIds.add(doctor.id);
        }
      }
      
      const filteredAppointments = currentHospitalId
        ? appointmentsRes.data.filter(a => a.hospital_id === currentHospitalId)
        : appointmentsRes.data;
      
      // Filter medical assistants by hospital
      const filteredMA = currentHospitalId
        ? maRes.data.filter(ma => ma.hospital_id === currentHospitalId)
        : maRes.data;
      
      setHospitals(filteredHospitals);
      setFrontDeskStaff(filteredStaff);
      setDoctors(uniqueDoctors);
      setAppointments(filteredAppointments);
      setMedicalAssistants(filteredMA);
      
      // Set default hospital_id if hospitals exist
      if (filteredHospitals.length > 0) {
        setStaffForm(prev => ({ ...prev, hospital_id: filteredHospitals[0].id }));
        setDoctorForm(prev => ({ ...prev, hospital_id: filteredHospitals[0].id }));
        setMAForm(prev => ({ ...prev, hospital_id: filteredHospitals[0].id }));
        
        // Fetch hospital settings
        try {
          const settingsRes = await axios.get(`${API}/hospitals/${filteredHospitals[0].id}/settings`);
          if (settingsRes.data) {
            if (settingsRes.data.show_hospital_branding_in_print !== undefined) {
              setShowHospitalBrandingInPrint(settingsRes.data.show_hospital_branding_in_print);
            }
            if (settingsRes.data.show_doctor_info_in_print !== undefined) {
              setShowDoctorInfoInPrint(settingsRes.data.show_doctor_info_in_print);
            }
            if (settingsRes.data.show_hospital_branding_in_pdf !== undefined) {
              setShowHospitalBrandingInPdf(settingsRes.data.show_hospital_branding_in_pdf);
            }
          }
        } catch (error) {
          // Settings might not exist yet, use default
          console.log('Settings not found, using defaults');
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    }
  };

  // Generate Hospital ID: [2 state code][3 city letters][3 numbers]
  const generateHospitalId = (stateCode, city) => {
    const cityCode = city.substring(0, 3).toUpperCase();
    const randomNum = Math.floor(100 + Math.random() * 900); // 3 digit number
    return `${stateCode}${cityCode}${randomNum}`;
  };

  // Generate Staff ID: FD + [last 3 of clinic ID] + [3 numbers]
  const generateStaffId = (hospitalCode) => {
    // Extract last 3 digits from hospital code (e.g., tshyd304 -> 304)
    const last3Digits = hospitalCode.match(/\d{3}$/)?.[0] || '000';
    const randomNum = Math.floor(100 + Math.random() * 900);
    return `FD${last3Digits}${randomNum}`;
  };

  // Generate Medical Assistant ID: MA + [last 3 of clinic ID] + [3 numbers]
  const generateMAId = (hospitalCode) => {
    // Extract last 3 digits from hospital code (e.g., tshyd304 -> 304)
    const last3Digits = hospitalCode.match(/\d{3}$/)?.[0] || '000';
    const randomNum = Math.floor(100 + Math.random() * 900);
    return `MA${last3Digits}${randomNum}`;
  };

  // Generate Doctor ID: DOC + [registered number]
  const generateDoctorId = (registeredNumber) => {
    return `DOC${registeredNumber}`;
  };

  const handleLogout = () => {
    localStorage.removeItem('healthcareUser');
    toast.success('Logged out successfully');
    navigate('/healthcare-login');
  };

  // Hospital Management
  const handleSaveHospital = async () => {
    try {
      // Validation
      if (!hospitalForm.name || !hospitalForm.address_line1 || !hospitalForm.city || 
          !hospitalForm.state || !hospitalForm.pincode) {
        toast.error('Please fill all required fields');
        return;
      }

      // Validate city has at least 3 letters (needed for hospital ID generation: STATE(2) + CITY(3) + RANDOM(3))
      if (hospitalForm.city.length < 3) {
        toast.error('City name must have at least 3 letters (used for Hospital ID generation)');
        return;
      }

      // For new hospital, check if one already exists
      if (!editingHospital && hospitals.length > 0) {
        toast.error('Only one hospital can be added. Please edit the existing hospital.');
        return;
      }

      // Upload logo if selected
      let logoUrl = hospitalForm.logo_url;
      if (selectedHospitalLogo) {
        const logoFormData = new FormData();
        logoFormData.append('file', selectedHospitalLogo);
        
        try {
          const uploadResponse = await axios.post(`${API}/upload-image`, logoFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          logoUrl = uploadResponse.data.url;
        } catch (error) {
          console.error('Logo upload error:', error);
          toast.error('Failed to upload logo: ' + (error.response?.data?.detail || error.message));
          return;
        }
      }

      // Construct full address for legacy compatibility
      const addressLines = [hospitalForm.address_line1];
      if (hospitalForm.address_line2) {
        addressLines.push(hospitalForm.address_line2);
      }
      addressLines.push(`${hospitalForm.city}, ${hospitalForm.state} ${hospitalForm.pincode}`);
      
      const hospitalData = {
        name: hospitalForm.name,
        address: addressLines.join('\n'),  // Legacy field
        address_line1: hospitalForm.address_line1,
        address_line2: hospitalForm.address_line2 || '',
        city: hospitalForm.city,
        state: hospitalForm.state,
        pincode: hospitalForm.pincode,
        country_code: hospitalForm.country_code || '+91',
        contact: hospitalForm.contact || '',
        logo_url: logoUrl || ''
      };

      if (editingHospital) {
        // Update existing hospital (keep the same ID and required fields)
        const updateData = {
          ...hospitalData,
          code: editingHospital.code,
          password: editingHospital.password
        };
        await axios.put(`${API}/hospitals/${editingHospital.id}`, updateData);
        toast.success('Hospital updated successfully');
      } else {
        // Create new hospital with generated ID using state code
        const hospitalId = generateHospitalId(hospitalForm.state, hospitalForm.city);
        
        // Check if ID already exists
        const existingHospitals = await axios.get(`${API}/hospitals`);
        if (existingHospitals.data.find(h => h.id === hospitalId)) {
          toast.error('Hospital ID already exists. Please try again.');
          return;
        }

        await axios.post(`${API}/hospitals`, { ...hospitalData, id: hospitalId });
        toast.success(`Hospital created with ID: ${hospitalId}`);
      }
      
      setShowHospitalDialog(false);
      setEditingHospital(null);
      setHospitalForm({ 
        name: '', 
        address_line1: '', 
        address_line2: '', 
        city: '', 
        state: '', 
        pincode: '',
        contact: '',
        country_code: '+91',
        logo_url: ''
      });
      setSelectedHospitalLogo(null);
      loadData();
    } catch (error) {
      console.error('Error saving hospital:', error);
      toast.error(error.response?.data?.detail || 'Failed to save hospital');
    }
  };

  const handleEditHospital = (hospital) => {
    setEditingHospital(hospital);
    
    // Check if hospital has new detailed address fields or legacy address field
    if (hospital.address_line1 || hospital.city || hospital.state) {
      // Use new detailed address fields
      setHospitalForm({ 
        name: hospital.name || '', 
        address_line1: hospital.address_line1 || '',
        address_line2: hospital.address_line2 || '',
        city: hospital.city || '',
        state: hospital.state || '',
        pincode: hospital.pincode || '',
        country_code: hospital.country_code || '+91',
        contact: hospital.contact || hospital.contact_phone || '',
        logo_url: hospital.logo_url || ''
      });
    } else {
      // Parse legacy address field for backward compatibility
      const addressParts = (hospital.address || '').split('\n').filter(line => line.trim());
      const lastLine = addressParts[addressParts.length - 1] || '';
      const cityStatePincode = lastLine.split(', ');
      const statePincode = cityStatePincode[1] ? cityStatePincode[1].split(' ') : ['', ''];
      
      setHospitalForm({ 
        name: hospital.name || '', 
        address_line1: addressParts[0] || '',
        address_line2: addressParts.length > 2 ? addressParts[1] : '',
        city: cityStatePincode[0] || '',
        state: statePincode[0] || '',
        pincode: statePincode[1] || '',
        country_code: '+91',
        contact: hospital.contact_phone || hospital.contact || '',
        logo_url: hospital.logo_url || ''
      });
    }
    setSelectedHospitalLogo(null);
    setShowHospitalDialog(true);
  };

  const handleDeleteHospital = async (id) => {
    if (!window.confirm('Are you sure you want to delete this hospital?')) return;
    
    try {
      await axios.delete(`${API}/hospitals/${id}`);
      toast.success('Hospital deleted successfully');
      loadData();
    } catch (error) {
      console.error('Error deleting hospital:', error);
      toast.error('Failed to delete hospital');
    }
  };

  // Front Desk Staff Management
  const handleSaveStaff = async () => {
    try {
      // Validation: phone is mandatory, email is optional, password required for new staff
      if (!staffForm.name || !staffForm.phone) {
        toast.error('Name and Phone are required');
        return;
      }

      if (!editingStaff && !staffForm.password) {
        toast.error('Password is required for new staff');
        return;
      }

      const staffData = {
        name: staffForm.name,
        email: staffForm.email || '',
        phone: staffForm.phone,
        hospital_id: staffForm.hospital_id
      };

      if (editingStaff) {
        // CRITICAL FIX: Include existing password when updating to prevent deletion
        staffData.password = editingStaff.password;
        await axios.put(`${API}/front-desk-staff/${editingStaff.id}`, staffData);
        toast.success('Staff updated successfully');
      } else {
        // Generate staff ID using hospital code (not internal ID)
        const currentHospital = hospitals.find(h => h.id === staffForm.hospital_id);
        const hospitalCode = currentHospital?.code || '';
        const staffId = generateStaffId(hospitalCode);
        await axios.post(`${API}/front-desk-staff`, { 
          ...staffData, 
          id: staffId,
          password: staffForm.password 
        });
        toast.success(`Staff created with ID: ${staffId}`);
      }
      
      setShowStaffDialog(false);
      setEditingStaff(null);
      setStaffForm({ name: '', email: '', phone: '', password: '', hospital_id: hospitals[0]?.id || '', country_code: '+91' });
      loadData();
    } catch (error) {
      console.error('Error saving staff:', error);
      toast.error(error.response?.data?.detail || 'Failed to save staff');
    }
  };

  const handleEditStaff = (staff) => {
    setEditingStaff(staff);
    setStaffForm({
      name: staff.name,
      email: staff.email,
      phone: staff.phone || '',
      password: '', // Password not needed for editing
      hospital_id: staff.hospital_id,
      country_code: staff.country_code || '+91'
    });
    setShowStaffDialog(true);
  };

  const handleDeleteStaff = async (id) => {
    if (!window.confirm('Are you sure you want to delete this staff member? It will be moved to deleted records and can be restored by Central Admin.')) return;
    
    const adminData = JSON.parse(localStorage.getItem('hospitalAdmin') || '{}');
    
    try {
      await axios.delete(`${API}/front-desk-staff/${id}`, {
        params: {
          deleted_by_id: adminData.id || 'hospital_admin',
          deleted_by_name: adminData.name || 'Hospital Admin',
          deleted_by_role: 'hospital_admin'
        }
      });
      toast.success('Staff deleted successfully');
      loadData();
    } catch (error) {
      console.error('Error deleting staff:', error);
      toast.error('Failed to delete staff');
    }
  };

  // Medical Assistant Management
  const handleSaveMA = async () => {
    try {
      // Validation: phone is mandatory, email is optional, password required for new MA
      if (!maForm.name || !maForm.phone) {
        toast.error('Name and phone number are required');
        return;
      }

      if (!editingMA && !maForm.password) {
        toast.error('Password is required for new Medical Assistant');
        return;
      }

      const maData = {
        name: maForm.name,
        email: maForm.email || '',
        phone: maForm.phone,
        hospital_id: maForm.hospital_id,
        doctor_ids: maForm.doctor_ids || []
      };

      if (editingMA) {
        // Update existing
        maData.password = editingMA.password;
        await axios.put(`${API}/medical-assistants/${editingMA.id}`, maData);
        toast.success('Medical Assistant updated successfully');
      } else {
        // Generate MA ID using hospital code (not internal ID)
        const currentHospital = hospitals.find(h => h.id === maForm.hospital_id);
        const hospitalCode = currentHospital?.code || '';
        const maId = generateMAId(hospitalCode);
        await axios.post(`${API}/medical-assistants`, { 
          ...maData, 
          id: maId,
          password: maForm.password 
        });
        toast.success(`Medical Assistant created with ID: ${maId}`);
      }
      
      setShowMADialog(false);
      setEditingMA(null);
      setMAForm({ name: '', email: '', phone: '', password: '', hospital_id: hospitals[0]?.id || '', doctor_ids: [], country_code: '+91' });
      loadData();
    } catch (error) {
      console.error('Error saving Medical Assistant:', error);
      toast.error(error.response?.data?.detail || 'Failed to save Medical Assistant');
    }
  };

  const handleEditMA = (ma) => {
    setEditingMA(ma);
    setMAForm({
      name: ma.name,
      email: ma.email || '',
      phone: ma.phone || '',
      password: '', // Password not needed for editing
      hospital_id: ma.hospital_id,
      doctor_ids: ma.doctor_ids || [],
      country_code: ma.country_code || '+91'
    });
    setShowMADialog(true);
  };

  const handleDeleteMA = async (id) => {
    if (!window.confirm('Are you sure you want to delete this Medical Assistant? It will be moved to deleted records and can be restored by Central Admin.')) return;
    
    const adminData = JSON.parse(localStorage.getItem('hospitalAdmin') || '{}');
    
    try {
      await axios.delete(`${API}/medical-assistants/${id}`, {
        params: {
          deleted_by_id: adminData.id || 'hospital_admin',
          deleted_by_name: adminData.name || 'Hospital Admin',
          deleted_by_role: 'hospital_admin'
        }
      });
      toast.success('Medical Assistant deleted successfully');
      loadData();
    } catch (error) {
      console.error('Error deleting Medical Assistant:', error);
      toast.error('Failed to delete Medical Assistant');
    }
  };

  // Doctor Management
  const handlePhotoUpload = async () => {
    if (!selectedPhoto) return '';

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedPhoto);

      const response = await axios.post(`${API}/upload-image/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      return response.data.image_url;
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
      return '';
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Search for existing doctor by registration number
  const handleSearchDoctor = async () => {
    if (!doctorForm.registered_number || doctorForm.registered_number.trim() === '') {
      toast.error('Please enter registration number to search');
      return;
    }

    setSearchingDoctor(true);
    try {
      const response = await axios.get(`${API}/doctors/search-by-registration`, {
        params: { registration_number: doctorForm.registered_number.trim() }
      });
      
      // Doctor found - populate form with existing data
      const doctor = response.data;
      setSearchedDoctor(doctor);
      setIsExistingDoctor(true);
      setDoctorForm({
        ...doctorForm,
        name: doctor.name,
        specialization: doctor.specialization,
        super_specialization: doctor.super_specialization || '',
        contact: doctor.contact || '',
        photo_url: doctor.photo_url || '',
        schedule: doctor.schedule || ''
      });
      toast.success('Doctor found! Review details and click Create to assign to hospital.');
    } catch (error) {
      if (error.response?.status === 404) {
        // Doctor not found - allow new entry
        setSearchedDoctor(null);
        setIsExistingDoctor(false);
        toast.info('No existing doctor found. Please fill in the details to create new doctor.');
      } else {
        console.error('Search error:', error);
        toast.error('Failed to search for doctor');
      }
    } finally {
      setSearchingDoctor(false);
    }
  };

  const handleSaveDoctor = async () => {
    try {
      // Validation
      if (!doctorForm.name || !doctorForm.specialization || !doctorForm.contact) {
        toast.error('Name, Specialization, and Contact are required');
        return;
      }

      if (!editingDoctor && !doctorForm.registered_number) {
        toast.error('Registered Number is required');
        return;
      }

      if (!editingDoctor && !doctorForm.password) {
        toast.error('Password is required for new doctors');
        return;
      }

      // Upload photo if selected
      let photoUrl = doctorForm.photo_url;
      if (selectedPhoto) {
        photoUrl = await handlePhotoUpload();
      }

      // Use the current logged-in hospital ID
      const currentHospitalId = hospital?.id;
      
      const doctorData = {
        registration_number: doctorForm.registered_number,
        name: doctorForm.name,
        specialization: doctorForm.specialization,
        super_specialization: doctorForm.super_specialization || null,
        contact: doctorForm.contact,
        password: doctorForm.password,
        photo_url: photoUrl || '',
        hospital_ids: [currentHospitalId], // Use array for multi-hospital support
        hospital_id: currentHospitalId, // Keep for backward compatibility
        schedule: doctorForm.schedule || ''
      };

      if (editingDoctor) {
        // Preserve existing hospital assignments when editing
        if (editingDoctor.hospital_ids) {
          doctorData.hospital_ids = editingDoctor.hospital_ids;
        }
        // CRITICAL FIX: Use existing password when updating to prevent deletion
        doctorData.password = editingDoctor.password;
        await axios.put(`${API}/doctors/${editingDoctor.id}`, doctorData);
        toast.success('Doctor updated successfully');
      } else {
        // Generate doctor ID
        const doctorId = generateDoctorId(doctorForm.registered_number);
        
        // Backend will handle if doctor exists (adds hospital) or creates new
        await axios.post(`${API}/doctors`, { ...doctorData, id: doctorId });
        
        if (isExistingDoctor) {
          toast.success('Existing doctor assigned to hospital');
        } else {
          // Show success message with doctor ID and password
          toast.success(
            `Doctor created successfully!\nDoctor ID: ${doctorId}\nPassword: ${doctorForm.password}`,
            { duration: 10000 }
          );
        }
      }
      
      setShowDoctorDialog(false);
      setEditingDoctor(null);
      setSelectedPhoto(null);
      setSearchedDoctor(null);
      setIsExistingDoctor(false);
      setDoctorForm({ 
        name: '', 
        specialization: '', 
        super_specialization: '',
        contact: '', 
        password: '',
        photo_url: '', 
        schedule: '',
        registered_number: '',
        country_code: '+91'
      });
      loadData();
    } catch (error) {
      console.error('Error saving doctor:', error);
      toast.error(error.response?.data?.detail || 'Failed to save doctor');
    }
  };

  const handleEditDoctor = (doctor) => {
    setEditingDoctor(doctor);
    setDoctorForm({
      name: doctor.name,
      specialization: doctor.specialization,
      super_specialization: doctor.super_specialization || '',
      contact: doctor.contact || '',
      password: '', // Password not editable in edit mode
      photo_url: doctor.photo_url || '',
      schedule: doctor.schedule || '',
      registered_number: doctor.id.replace('DOC', '') // Extract registered number from ID
    });
    setShowDoctorDialog(true);
  };

  const handleDeleteDoctor = async (id) => {
    try {
      // Get doctor to check their hospital assignments
      const doctorResponse = await axios.get(`${API}/doctors/${id}`);
      const doctor = doctorResponse.data;
      const hospitalIds = doctor.hospital_ids || (doctor.hospital_id ? [doctor.hospital_id] : []);
      
      let confirmMessage = '';
      
      if (hospitalIds.length === 1 && hospitalIds.includes(hospital?.id)) {
        // This is doctor's last hospital
        confirmMessage = 'Remove this doctor from your hospital? This is their only assignment, so they will become unassigned (but will remain in the system for future assignment).';
      } else if (hospitalIds.length > 1) {
        confirmMessage = `Remove this doctor from your hospital? They will still work at ${hospitalIds.length - 1} other hospital(s).`;
      } else {
        confirmMessage = 'Remove this doctor from your hospital?';
      }
      
      if (!window.confirm(confirmMessage)) return;
      
      // Admin dashboard can ONLY unassign, never permanently delete
      const response = await axios.delete(`${API}/doctors/${id}`, {
        params: {
          hospital_id: hospital?.id,
          force_delete: false  // Never permanently delete from admin dashboard
        }
      });
      
      if (response.data.unassigned) {
        toast.success('Doctor removed from hospital (now unassigned)');
      } else {
        toast.success('Doctor removed from hospital');
      }
      
      loadData();
    } catch (error) {
      console.error('Error removing doctor:', error);
      toast.error('Failed to remove doctor');
    }
  };

  const calculateAge = (dob) => {
    const birthDate = new Date(dob.split('/').reverse().join('-'));
    const today = new Date();
    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    let days = today.getDate() - birthDate.getDate();

    if (days < 0) {
      months--;
      days += new Date(today.getFullYear(), today.getMonth(), 0).getDate();
    }
    if (months < 0) {
      years--;
      months += 12;
    }

    return `${years}y ${months}m ${days}d`;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Mobile Responsive */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 md:px-6 py-2 md:py-3">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <h1 className="text-lg md:text-2xl font-bold" style={{fontFamily: 'Space Grotesk'}}>
                Hospital Admin Dashboard
              </h1>
              <div className="flex items-start space-x-3 md:space-x-4 mt-1">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mt-1">
                  <Building2 className="w-5 h-5 md:w-6 md:h-6" />
                </div>
                <div>
                  {hospital && (
                    <p className="text-blue-100 text-sm md:text-lg font-bold">{hospital.name}</p>
                  )}
                  <p className="text-blue-100 text-xs md:text-base font-medium">{user.name}</p>
                </div>
              </div>
            </div>
            
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-white hover:bg-white hover:bg-opacity-20 text-sm md:text-base px-2 md:px-4"
            >
              <LogOut className="w-4 h-4 md:w-5 md:h-5 md:mr-2" />
              <span className="hidden md:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs - Mobile Responsive with Horizontal Scroll */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex space-x-4 md:space-x-8 overflow-x-auto scrollbar-hide">
            {[
              { id: 'hospital', label: 'Hospital Info', icon: Building2 },
              { id: 'frontdesk', label: 'Front Desk Staff', icon: Users },
              { id: 'medical-assistants', label: 'Medical Assistants', icon: HeartPulse },
              { id: 'doctors', label: 'Doctors', icon: Stethoscope },
              { id: 'configuration', label: 'Configuration', icon: Settings },
              { id: 'statistics', label: 'Statistics', icon: BarChart3 },
              { id: 'security', label: 'Security', icon: Shield },
              { id: 'settings', label: 'Settings', icon: Settings }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-3 md:py-4 px-3 md:px-2 border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="font-medium text-sm md:text-base">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content - Mobile Responsive */}
      <div className="container mx-auto px-4 md:px-6 py-6 md:py-8">
        {activeTab === 'hospital' && (
          <div className="space-y-4 md:space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">Hospital/Clinic Management</h2>
              {hospitals.length === 0 && (
                <Button
                  onClick={() => {
                    setEditingHospital(null);
                    setHospitalForm({ name: '', address_line1: '', address_line2: '', city: '', state: '', pincode: '', contact: '', country_code: '+91' });
                    setShowHospitalDialog(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Hospital
                </Button>
              )}
            </div>

            <div className="grid gap-4">
              {hospitals.map(hospital => (
                <div key={hospital.id} className="bg-white rounded-xl shadow-sm border p-4 md:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:space-x-3 mb-3">
                        <h3 className="text-lg md:text-xl font-bold text-gray-900">{hospital.name}</h3>
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs md:text-sm font-mono w-fit">
                          {hospital.code || hospital.id.substring(0, 8).toUpperCase()}
                        </span>
                      </div>
                      {hospital.address && (
                        <p className="text-sm md:text-base text-gray-600 mb-1 whitespace-pre-line">📍 {hospital.address}</p>
                      )}
                      {hospital.contact && (
                        <p className="text-sm md:text-base text-gray-600">📞 {hospital.contact}</p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditHospital(hospital)}
                        className="text-blue-600 hover:text-blue-700"
                        data-testid="edit-hospital-btn"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {hospitals.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Building2 className="w-12 h-12 md:w-16 md:h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-sm md:text-base">No hospitals added yet. Click "Add Hospital" to get started.</p>
                  <p className="text-xs md:text-sm mt-2">Note: Only one hospital can be added.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'frontdesk' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Front Desk Staff</h2>
              <Button
                onClick={() => {
                  setEditingStaff(null);
                  setStaffForm({ name: '', email: '', phone: '', hospital_id: hospitals[0]?.id || '', country_code: '+91' });
                  setShowStaffDialog(true);
                }}
                className="bg-green-600 hover:bg-green-700"
                disabled={hospitals.length === 0}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Staff
              </Button>
            </div>

            {hospitals.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
                <p className="text-yellow-800">Please add a hospital first before creating staff members.</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {frontDeskStaff.map(staff => (
                  <div key={staff.id} className="bg-white rounded-xl shadow-sm border p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{staff.name}</h3>
                        <p className="text-gray-600 mb-1">✉️ {staff.email}</p>
                        {staff.phone && <p className="text-gray-600 mb-1">📞 {staff.phone}</p>}
                        <p className="text-sm text-gray-500 mt-2">
                          Staff ID: <code className="bg-gray-100 px-2 py-1 rounded">{staff.id}</code>
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditStaff(staff)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteStaff(staff.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {frontDeskStaff.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p>No staff members added yet. Click "Add Staff" to get started.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'medical-assistants' && (
          <div className="space-y-6" data-testid="medical-assistants-tab">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Medical Assistants</h2>
              <Button
                onClick={() => {
                  setEditingMA(null);
                  setMAForm({ name: '', email: '', phone: '', password: '', hospital_id: hospitals[0]?.id || '', doctor_ids: [], country_code: '+91' });
                  setShowMADialog(true);
                }}
                className="bg-pink-600 hover:bg-pink-700"
                disabled={hospitals.length === 0}
                data-testid="add-ma-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Medical Assistant
              </Button>
            </div>

            {hospitals.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
                <p className="text-yellow-800">Please add a hospital first before creating Medical Assistants.</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {medicalAssistants.map(ma => (
                  <div key={ma.id} className="bg-white rounded-xl shadow-sm border p-6" data-testid={`ma-card-${ma.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full flex items-center justify-center">
                            <HeartPulse className="w-5 h-5 text-pink-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">{ma.name}</h3>
                            <p className="text-sm text-gray-500">
                              ID: <code className="bg-gray-100 px-2 py-0.5 rounded">{ma.id}</code>
                            </p>
                          </div>
                        </div>
                        {ma.email && <p className="text-gray-600 mb-1">✉️ {ma.email}</p>}
                        {ma.phone && <p className="text-gray-600 mb-1">📞 {ma.phone}</p>}
                        {ma.doctor_ids && ma.doctor_ids.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm text-gray-500 mb-1">Assigned Doctors:</p>
                            <div className="flex flex-wrap gap-1">
                              {ma.doctor_ids.map(docId => {
                                const doc = doctors.find(d => d.id === docId);
                                return (
                                  <span key={docId} className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-sm">
                                    {doc ? `Dr. ${doc.name}` : docId}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditMA(ma)}
                          className="text-blue-600 hover:text-blue-700"
                          data-testid={`edit-ma-${ma.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteMA(ma.id)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`delete-ma-${ma.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {medicalAssistants.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <HeartPulse className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p>No Medical Assistants added yet. Click "Add Medical Assistant" to get started.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'doctors' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Doctors</h2>
              <Button
                onClick={() => {
                  setEditingDoctor(null);
                  setDoctorForm({ 
                    name: '', 
                    specialization: '', 
                    contact: '', 
                    photo_url: '', 
                    hospital_id: hospitals[0]?.id || '',
                    schedule: ''
                  });
                  setShowDoctorDialog(true);
                }}
                className="bg-purple-600 hover:bg-purple-700"
                disabled={hospitals.length === 0}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Doctor
              </Button>
            </div>

            {hospitals.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
                <p className="text-yellow-800">Please add a hospital first before creating doctors.</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {doctors.map(doctor => (
                  <div key={doctor.id} className="bg-white rounded-xl shadow-sm border p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        {doctor.photo_url ? (
                          <img src={doctor.photo_url} alt={doctor.name} className="w-16 h-16 rounded-full object-cover" />
                        ) : (
                          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                            <Stethoscope className="w-8 h-8 text-purple-600" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 mb-1">Dr. {doctor.name}</h3>
                          <p className="text-purple-600 font-medium mb-2">
                            {doctor.super_specialization || doctor.specialization}
                          </p>
                          {doctor.contact && <p className="text-gray-600 mb-1">📞 {doctor.contact}</p>}
                          {doctor.schedule && <p className="text-gray-600 text-sm mb-1">⏰ {doctor.schedule}</p>}
                          {doctor.leave_dates && doctor.leave_dates.length > 0 && (() => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const futureDates = doctor.leave_dates.filter(date => {
                              const leaveDate = new Date(date);
                              leaveDate.setHours(0, 0, 0, 0);
                              return leaveDate >= today;
                            });
                            return futureDates.length > 0 ? (
                              <div className="text-orange-600 text-sm mb-1">
                                <span className="font-medium">🏖️ On Leave: </span>
                                <span>{formatLeaveDates(futureDates)}</span>
                              </div>
                            ) : null;
                          })()}
                          <p className="text-sm text-gray-500 mt-2">
                            Doctor ID: <code className="bg-gray-100 px-2 py-1 rounded">{doctor.id}</code>
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditDoctor(doctor)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDoctor(doctor.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {doctors.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <Stethoscope className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p>No doctors added yet. Click "Add Doctor" to get started.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'configuration' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Hospital Configuration</h2>
            </div>

            {/* Doctor Consultation Fees Configuration */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Doctor Consultation Fees</h3>
              <p className="text-sm text-gray-600 mb-4">Set consultation fees for each doctor at your clinic</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {doctors.map((doctor) => {
                  const currentFee = hospital?.doctor_fees?.[doctor.id] || '';
                  
                  return (
                    <div key={doctor.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">Dr. {doctor.name}</h4>
                          <p className="text-sm text-gray-600">
                            {doctor.super_specialization || doctor.specialization}
                          </p>
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor={`fee-${doctor.id}`} className="text-xs">Consultation Fee (₹)</Label>
                        <Input
                          id={`fee-${doctor.id}`}
                          type="number"
                          placeholder="e.g., 500"
                          value={currentFee}
                          onChange={(e) => {
                            const updatedFees = {
                              ...(hospital?.doctor_fees || {}),
                              [doctor.id]: e.target.value
                            };
                            setHospital({ ...hospital, doctor_fees: updatedFees });
                          }}
                          onBlur={async () => {
                            try {
                              const updatedFees = {
                                ...(hospital?.doctor_fees || {}),
                                [doctor.id]: currentFee ? parseFloat(currentFee) : null
                              };
                              
                              // Remove null values
                              Object.keys(updatedFees).forEach(key => {
                                if (updatedFees[key] === null || updatedFees[key] === '') {
                                  delete updatedFees[key];
                                }
                              });
                              
                              await axios.put(`${API}/hospitals/${hospital.id}/doctor-fees`, updatedFees);
                              toast.success(`Fee updated for Dr. ${doctor.name}`);
                              
                              // Reload hospital data
                              const response = await axios.get(`${API}/hospitals/${hospital.id}`);
                              setHospital(response.data);
                            } catch (error) {
                              console.error('Error updating doctor fee:', error);
                              toast.error('Failed to update doctor fee');
                            }
                          }}
                          className="mt-1"
                          min="0"
                          step="50"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {doctors.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Stethoscope className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No doctors added yet. Add doctors to set their consultation fees.</p>
                </div>
              )}
            </div>

          </div>
        )}

        {activeTab === 'statistics' && (
          <div className="space-y-6">
            {/* Header with Year Filter */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>
                Hospital Appointments ({selectedYear})
              </h3>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="statistics-year-filter"
              >
                {(() => {
                  const years = new Set();
                  appointments.forEach(apt => {
                    if (apt.appointment_date) {
                      let year;
                      if (apt.appointment_date.includes('/')) {
                        const parts = apt.appointment_date.split('/');
                        year = parseInt(parts[2]);
                      } else {
                        year = new Date(apt.appointment_date).getFullYear();
                      }
                      if (!isNaN(year)) years.add(year);
                    }
                  });
                  // Add current year and surrounding years
                  const currentYear = new Date().getFullYear();
                  for (let y = currentYear - 2; y <= currentYear + 2; y++) {
                    years.add(y);
                  }
                  const yearArray = Array.from(years).sort((a, b) => b - a);
                  return yearArray.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ));
                })()}
              </select>
            </div>

            {/* Statistics Cards */}
            {(() => {
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              
              // Helper to parse date and get year
              const getAppointmentYear = (apt) => {
                if (!apt.appointment_date) return null;
                if (apt.appointment_date.includes('/')) {
                  return parseInt(apt.appointment_date.split('/')[2]);
                }
                return new Date(apt.appointment_date).getFullYear();
              };
              
              // Helper to parse date and get month index
              const getAppointmentMonth = (apt) => {
                if (!apt.appointment_date) return null;
                if (apt.appointment_date.includes('/')) {
                  return parseInt(apt.appointment_date.split('/')[1]) - 1;
                }
                return new Date(apt.appointment_date).getMonth();
              };
              
              // Filter appointments by selected year
              const yearAppointments = appointments.filter(apt => getAppointmentYear(apt) === selectedYear);
              
              const totalCount = yearAppointments.length;
              const completedCount = yearAppointments.filter(apt => apt.status === 'completed').length;
              const pendingCount = yearAppointments.filter(apt => apt.status === 'scheduled' || apt.status === 'pending').length;
              const cancelledCount = yearAppointments.filter(apt => apt.status === 'cancelled').length;
              
              // Calculate monthly breakdown
              const monthlyData = {};
              monthNames.forEach(month => {
                monthlyData[month] = { total: 0, completed: 0, pending: 0, cancelled: 0 };
              });
              
              yearAppointments.forEach(apt => {
                const monthIndex = getAppointmentMonth(apt);
                if (monthIndex !== null && monthIndex >= 0 && monthIndex < 12) {
                  const monthName = monthNames[monthIndex];
                  monthlyData[monthName].total++;
                  if (apt.status === 'completed') monthlyData[monthName].completed++;
                  if (apt.status === 'scheduled' || apt.status === 'pending') monthlyData[monthName].pending++;
                  if (apt.status === 'cancelled') monthlyData[monthName].cancelled++;
                }
              });
              
              return (
                <>
                  {/* Count Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 shadow-sm border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-blue-700 font-medium mb-1">Total</p>
                          <p className="text-3xl font-bold text-blue-900">{totalCount}</p>
                          <p className="text-xs text-blue-600 mt-1">Appointments</p>
                        </div>
                        <Calendar className="w-10 h-10 text-blue-600" />
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 shadow-sm border border-green-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-green-700 font-medium mb-1">Completed</p>
                          <p className="text-3xl font-bold text-green-900">{completedCount}</p>
                          <p className="text-xs text-green-600 mt-1">Sessions done</p>
                        </div>
                        <CheckCircle className="w-10 h-10 text-green-600" />
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-6 shadow-sm border border-yellow-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-yellow-700 font-medium mb-1">Pending</p>
                          <p className="text-3xl font-bold text-yellow-900">{pendingCount}</p>
                          <p className="text-xs text-yellow-600 mt-1">Awaiting</p>
                        </div>
                        <Clock className="w-10 h-10 text-yellow-600" />
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6 shadow-sm border border-red-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-red-700 font-medium mb-1">Cancelled</p>
                          <p className="text-3xl font-bold text-red-900">{cancelledCount}</p>
                          <p className="text-xs text-red-600 mt-1">Not attended</p>
                        </div>
                        <Calendar className="w-10 h-10 text-red-600" />
                      </div>
                    </div>
                  </div>

                  {/* Monthly Breakdown Table */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-6 py-4 border-b border-gray-200">
                      <h4 className="font-semibold text-lg">{hospital?.name || 'Hospital'}</h4>
                      <p className="text-sm text-gray-600">
                        Year Total: {totalCount} | Completed: {completedCount} | Pending: {pendingCount} | Cancelled: {cancelledCount}
                      </p>
                    </div>
                    
                    <div className="p-4 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 px-2 font-semibold text-gray-700"></th>
                            {monthNames.map(month => (
                              <th key={month} className="text-center py-2 px-2 font-semibold text-gray-700">{month}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-2 font-medium text-gray-600">Total</td>
                            {monthNames.map(month => (
                              <td key={month} className="text-center py-2 px-2 text-gray-900 font-medium">
                                {monthlyData[month].total || 0}
                              </td>
                            ))}
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-2 font-medium text-gray-600">Completed</td>
                            {monthNames.map(month => (
                              <td key={month} className="text-center py-2 px-2 text-green-600 font-medium">
                                {monthlyData[month].completed || 0}
                              </td>
                            ))}
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-2 font-medium text-gray-600">Pending</td>
                            {monthNames.map(month => (
                              <td key={month} className="text-center py-2 px-2 text-yellow-600 font-medium">
                                {monthlyData[month].pending || 0}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td className="py-2 px-2 font-medium text-gray-600">Cancelled</td>
                            {monthNames.map(month => (
                              <td key={month} className="text-center py-2 px-2 text-red-600 font-medium">
                                {monthlyData[month].cancelled || 0}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Empty State */}
                  {totalCount === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p>No appointments found for {selectedYear}</p>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Security & Audit Tab */}
        {activeTab === 'security' && hospital && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                <Shield className="w-6 h-6 inline mr-2 text-blue-600" />
                Security & Audit Logs
              </h2>
            </div>
            <SecurityAuditTab 
              hospitals={[hospital]} 
              isHospitalAdmin={true} 
              hospitalId={hospital.id} 
            />
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Print Settings</h2>
              
              <div className="space-y-6">
                {/* Hospital Branding Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Show Hospital Branding in Print Preview
                    </h3>
                    <p className="text-sm text-gray-600">
                      When enabled, hospital name, logo, and address will be displayed in the print preview when printing prescriptions from session cards.
                    </p>
                  </div>
                  
                  <div className="ml-6">
                    <button
                      onClick={async () => {
                        const newValue = !showHospitalBrandingInPrint;
                        setShowHospitalBrandingInPrint(newValue);
                        setSavingSettings(true);
                        
                        try {
                          if (!hospital || !hospital.id) {
                            toast.error('Hospital information not available');
                            setShowHospitalBrandingInPrint(!newValue);
                            setSavingSettings(false);
                            return;
                          }
                          
                          await axios.put(`${API}/hospitals/${hospital.id}/settings`, {
                            show_hospital_branding_in_print: newValue
                          });
                          toast.success('Settings saved successfully');
                        } catch (error) {
                          console.error('Error saving settings:', error);
                          toast.error('Failed to save settings');
                          // Revert on error
                          setShowHospitalBrandingInPrint(!newValue);
                        } finally {
                          setSavingSettings(false);
                        }
                      }}
                      disabled={savingSettings}
                      className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                        showHospitalBrandingInPrint ? 'bg-purple-600' : 'bg-gray-300'
                      } ${savingSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span
                        className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                          showHospitalBrandingInPrint ? 'translate-x-7' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Doctor Information Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Show Doctor Information in Print Preview
                    </h3>
                    <p className="text-sm text-gray-600">
                      When enabled, doctor name, specialization, and registration number will be displayed in the print preview. Disable this to use your own pre-printed header paper.
                    </p>
                  </div>
                  
                  <div className="ml-6">
                    <button
                      onClick={async () => {
                        const newValue = !showDoctorInfoInPrint;
                        setShowDoctorInfoInPrint(newValue);
                        setSavingSettings(true);
                        
                        try {
                          if (!hospital || !hospital.id) {
                            toast.error('Hospital information not available');
                            setShowDoctorInfoInPrint(!newValue);
                            setSavingSettings(false);
                            return;
                          }
                          
                          await axios.put(`${API}/hospitals/${hospital.id}/settings`, {
                            show_doctor_info_in_print: newValue
                          });
                          toast.success('Settings saved successfully');
                        } catch (error) {
                          console.error('Error saving settings:', error);
                          toast.error('Failed to save settings');
                          // Revert on error
                          setShowDoctorInfoInPrint(!newValue);
                        } finally {
                          setSavingSettings(false);
                        }
                      }}
                      disabled={savingSettings}
                      className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                        showDoctorInfoInPrint ? 'bg-purple-600' : 'bg-gray-300'
                      } ${savingSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span
                        className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                          showDoctorInfoInPrint ? 'translate-x-7' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
                
                <div className="text-sm text-gray-500 italic">
                  Note: These settings only affect the print preview when using the Print button in session cards.
                </div>
              </div>
            </div>

            {/* PDF Settings */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">PDF Download Settings</h2>
              
              <div className="space-y-6">
                {/* Hospital Branding in PDF Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Show Hospital Branding in Downloaded PDF
                    </h3>
                    <p className="text-sm text-gray-600">
                      When enabled, hospital logo, name, and address will be displayed in downloaded prescription PDFs. Disable this if you're using pre-printed letterhead or prescription background.
                    </p>
                  </div>
                  
                  <div className="ml-6">
                    <button
                      onClick={async () => {
                        const newValue = !showHospitalBrandingInPdf;
                        setShowHospitalBrandingInPdf(newValue);
                        setSavingSettings(true);
                        
                        try {
                          if (!hospital || !hospital.id) {
                            toast.error('Hospital information not available');
                            setShowHospitalBrandingInPdf(!newValue);
                            setSavingSettings(false);
                            return;
                          }
                          
                          await axios.put(`${API}/hospitals/${hospital.id}/settings`, {
                            show_hospital_branding_in_pdf: newValue
                          });
                          toast.success('Settings saved successfully');
                        } catch (error) {
                          console.error('Error saving settings:', error);
                          toast.error('Failed to save settings');
                          // Revert on error
                          setShowHospitalBrandingInPdf(!newValue);
                        } finally {
                          setSavingSettings(false);
                        }
                      }}
                      disabled={savingSettings}
                      className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                        showHospitalBrandingInPdf ? 'bg-purple-600' : 'bg-gray-300'
                      } ${savingSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span
                        className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                          showHospitalBrandingInPdf ? 'translate-x-7' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
                
                <div className="text-sm text-gray-500 italic">
                  Note: This setting affects the PDF generated when clicking "Download as PDF" button in prescription view.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hospital Dialog */}
      <Dialog open={showHospitalDialog} onOpenChange={setShowHospitalDialog}>
        <DialogContent className="sm:max-w-md z-50 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingHospital ? 'Edit Hospital' : 'Add Hospital/Clinic'}</DialogTitle>
            <DialogDescription>
              {editingHospital ? 'Update hospital information' : 'Add a new hospital or clinic to the system'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="hospital_name">Hospital/Clinic Name *</Label>
              <Input
                id="hospital_name"
                value={hospitalForm.name}
                onChange={(e) => setHospitalForm({...hospitalForm, name: e.target.value})}
                placeholder="e.g., City General Hospital"
                required
                disabled={editingHospital}
                className={editingHospital ? 'bg-gray-100 cursor-not-allowed' : ''}
              />
              {editingHospital && (
                <p className="text-xs text-gray-500 mt-1">Hospital name cannot be edited by Hospital Admin</p>
              )}
            </div>
            <div>
              <Label htmlFor="address_line1">Address Line 1 *</Label>
              <Input
                id="address_line1"
                value={hospitalForm.address_line1}
                onChange={(e) => setHospitalForm({...hospitalForm, address_line1: e.target.value})}
                placeholder="e.g., Building No, Street Name"
                required
              />
            </div>
            <div>
              <Label htmlFor="address_line2">Address Line 2</Label>
              <Input
                id="address_line2"
                value={hospitalForm.address_line2}
                onChange={(e) => setHospitalForm({...hospitalForm, address_line2: e.target.value})}
                placeholder="e.g., Area, Landmark"
              />
            </div>
            <div>
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                value={hospitalForm.city}
                onChange={(e) => setHospitalForm({...hospitalForm, city: e.target.value})}
                placeholder="e.g., Mumbai"
                required
                disabled={editingHospital}
                className={editingHospital ? 'bg-gray-100 cursor-not-allowed' : ''}
              />
              {editingHospital && (
                <p className="text-xs text-gray-500 mt-1">City cannot be edited by Hospital Admin</p>
              )}
            </div>
            <div>
              <Label htmlFor="state">State *</Label>
              <select
                id="state"
                value={hospitalForm.state}
                onChange={(e) => setHospitalForm({...hospitalForm, state: e.target.value})}
                className={`w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm ${editingHospital ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                required
                disabled={editingHospital}
              >
                <option value="">Select State</option>
                {indianStates.map(state => (
                  <option key={state.code} value={state.code}>
                    {state.code} ({state.name})
                  </option>
                ))}
              </select>
              {editingHospital && (
                <p className="text-xs text-gray-500 mt-1">State cannot be edited by Hospital Admin</p>
              )}
            </div>
            <div>
              <Label htmlFor="pincode">Pincode *</Label>
              <Input
                id="pincode"
                value={hospitalForm.pincode}
                onChange={(e) => setHospitalForm({...hospitalForm, pincode: e.target.value})}
                placeholder="e.g., 400001"
                required
              />
            </div>
            <div>
              <Label htmlFor="hospital_contact">Contact Phone *</Label>
              <PhoneInput
                value={hospitalForm.contact}
                onChange={(value) => setHospitalForm({...hospitalForm, contact: value})}
                countryCode={hospitalForm.country_code}
                onCountryCodeChange={(code) => setHospitalForm({...hospitalForm, country_code: code})}
                placeholder="Enter 10-digit mobile number"
                required
              />
            </div>
            <div>
              <Label htmlFor="hospital_logo">Hospital Logo (Optional)</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  id="hospital_logo"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setSelectedHospitalLogo(e.target.files[0]);
                    }
                  }}
                  className="hidden"
                />
                <label htmlFor="hospital_logo" className="cursor-pointer">
                  <div className="flex flex-col items-center">
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Click to upload logo</p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 2MB</p>
                  </div>
                </label>
                {(selectedHospitalLogo || hospitalForm.logo_url) && (
                  <div className="mt-3 flex items-center justify-center">
                    <img 
                      src={selectedHospitalLogo 
                        ? URL.createObjectURL(selectedHospitalLogo) 
                        : (hospitalForm.logo_url.startsWith('http') 
                          ? hospitalForm.logo_url 
                          : `${BACKEND_URL}${hospitalForm.logo_url}`)} 
                      alt="Hospital logo preview" 
                      className="h-20 w-20 object-contain border rounded"
                      onError={(e) => {
                        console.error('Failed to load hospital logo:', hospitalForm.logo_url);
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            {!editingHospital && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <p className="font-medium">Hospital ID will be auto-generated:</p>
                <p className="font-mono mt-1">
                  {hospitalForm.state && hospitalForm.city 
                    ? `${hospitalForm.state}${hospitalForm.city.substring(0,3).toUpperCase()}XXX`
                    : 'Select state and enter city to see format'}
                </p>
              </div>
            )}
            <div className="flex space-x-2">
              <Button 
                onClick={handleSaveHospital} 
                className="flex-1" 
                disabled={!hospitalForm.name || !hospitalForm.address_line1 || !hospitalForm.city || !hospitalForm.state || !hospitalForm.pincode}
              >
                {editingHospital ? 'Update' : 'Create'}
              </Button>
              <Button variant="outline" onClick={() => {
                setShowHospitalDialog(false);
                setEditingHospital(null);
              }} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Front Desk Staff Dialog */}
      <Dialog open={showStaffDialog} onOpenChange={setShowStaffDialog}>
        <DialogContent className="max-w-md z-50">
          <DialogHeader>
            <DialogTitle>{editingStaff ? 'Edit Staff' : 'Add Front Desk Staff'}</DialogTitle>
            <DialogDescription>
              {editingStaff ? 'Update staff member information' : 'Add a new front desk staff member to this hospital'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="staff_name">Name *</Label>
              <Input
                id="staff_name"
                value={staffForm.name}
                onChange={(e) => setStaffForm({...staffForm, name: e.target.value})}
                placeholder="e.g., John Doe"
                required
              />
            </div>
            <div>
              <Label htmlFor="staff_phone">Phone Number *</Label>
              <PhoneInput
                value={staffForm.phone}
                onChange={(value) => setStaffForm({...staffForm, phone: value})}
                countryCode={staffForm.country_code}
                onCountryCodeChange={(code) => setStaffForm({...staffForm, country_code: code})}
                placeholder="Enter 10-digit mobile number"
                required
              />
            </div>
            {!editingStaff && (
              <div>
                <Label htmlFor="staff_password">Password *</Label>
                <Input
                  id="staff_password"
                  type="password"
                  value={staffForm.password}
                  onChange={(e) => setStaffForm({...staffForm, password: e.target.value})}
                  placeholder="Enter password for staff login"
                  required
                />
              </div>
            )}
            <div>
              <Label htmlFor="staff_email">Email (Optional)</Label>
              <Input
                id="staff_email"
                type="email"
                value={staffForm.email}
                onChange={(e) => setStaffForm({...staffForm, email: e.target.value})}
                placeholder="e.g., john@hospital.com"
              />
            </div>
            <div>
              <Label>Hospital</Label>
              <div className="p-2 bg-gray-50 rounded border border-gray-200">
                <p className="text-sm font-medium">
                  {hospitals.find(h => h.id === staffForm.hospital_id)?.name || 'Loading...'}
                </p>
              </div>
            </div>
            {!editingStaff && staffForm.hospital_id && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                <p className="font-medium">Staff ID will be auto-generated:</p>
                <p className="font-mono mt-1">
                  FD{hospitals.find(h => h.id === staffForm.hospital_id)?.code.match(/\d{3}$/)?.[0] || '000'}XXX
                </p>
                <p className="text-xs mt-1 text-green-700">
                  (Last 3 digits from Hospital ID: {hospitals.find(h => h.id === staffForm.hospital_id)?.code})
                </p>
              </div>
            )}
            <div className="flex space-x-2">
              <Button 
                onClick={handleSaveStaff} 
                className="flex-1 bg-purple-600 hover:bg-purple-700" 
                disabled={!staffForm.name || !staffForm.phone || (!editingStaff && !staffForm.password)}
              >
                {editingStaff ? 'Update' : 'Add Staff'}
              </Button>
              <Button variant="outline" onClick={() => {
                setShowStaffDialog(false);
                setEditingStaff(null);
              }} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Medical Assistant Dialog */}
      <Dialog open={showMADialog} onOpenChange={setShowMADialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto z-50" data-testid="ma-dialog">
          <DialogHeader>
            <DialogTitle>{editingMA ? 'Edit Medical Assistant' : 'Add Medical Assistant'}</DialogTitle>
            <DialogDescription>
              {editingMA ? 'Update Medical Assistant information' : 'Add a new Medical Assistant to this hospital'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="ma_name">Name *</Label>
              <Input
                id="ma_name"
                value={maForm.name}
                onChange={(e) => setMAForm({...maForm, name: e.target.value})}
                placeholder="e.g., Jane Smith"
                required
                data-testid="ma-name-input"
              />
            </div>
            <div>
              <Label htmlFor="ma_phone">Phone Number *</Label>
              <PhoneInput
                value={maForm.phone}
                onChange={(value) => setMAForm({...maForm, phone: value})}
                countryCode={maForm.country_code}
                onCountryCodeChange={(code) => setMAForm({...maForm, country_code: code})}
                placeholder="Enter 10-digit mobile number"
                required
              />
            </div>
            {!editingMA && (
              <div>
                <Label htmlFor="ma_password">Password *</Label>
                <Input
                  id="ma_password"
                  type="password"
                  value={maForm.password}
                  onChange={(e) => setMAForm({...maForm, password: e.target.value})}
                  placeholder="Enter password for login"
                  required
                  data-testid="ma-password-input"
                />
              </div>
            )}
            <div>
              <Label htmlFor="ma_email">Email (Optional)</Label>
              <Input
                id="ma_email"
                type="email"
                value={maForm.email}
                onChange={(e) => setMAForm({...maForm, email: e.target.value})}
                placeholder="e.g., jane@hospital.com"
              />
            </div>
            <div>
              <Label>Assign to Doctors</Label>
              <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                {doctors.length === 0 ? (
                  <p className="text-sm text-gray-500">No doctors available. Add doctors first.</p>
                ) : (
                  doctors.map(doc => (
                    <label key={doc.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={maForm.doctor_ids.includes(doc.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setMAForm({...maForm, doctor_ids: [...maForm.doctor_ids, doc.id]});
                          } else {
                            setMAForm({...maForm, doctor_ids: maForm.doctor_ids.filter(id => id !== doc.id)});
                          }
                        }}
                        className="w-4 h-4 text-pink-600 rounded border-gray-300"
                      />
                      <span className="text-sm">Dr. {doc.name}</span>
                      <span className="text-xs text-gray-400">({doc.specialization})</span>
                    </label>
                  ))
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">Select doctors whose appointments this MA can view</p>
            </div>
            <div>
              <Label>Hospital</Label>
              <div className="p-2 bg-gray-50 rounded border border-gray-200">
                <p className="text-sm font-medium">
                  {hospitals.find(h => h.id === maForm.hospital_id)?.name || 'Loading...'}
                </p>
              </div>
            </div>
            {!editingMA && maForm.hospital_id && (
              <div className="bg-pink-50 border border-pink-200 rounded-lg p-3 text-sm text-pink-800">
                <p className="font-medium">Staff ID will be auto-generated:</p>
                <p className="font-mono mt-1">
                  MA{hospitals.find(h => h.id === maForm.hospital_id)?.code.match(/\d{3}$/)?.[0] || '000'}XXX
                </p>
                <p className="text-xs mt-1 text-pink-700">
                  (Last 3 digits from Hospital ID: {hospitals.find(h => h.id === maForm.hospital_id)?.code})
                </p>
              </div>
            )}
            <div className="flex space-x-2">
              <Button 
                onClick={handleSaveMA} 
                className="flex-1 bg-pink-600 hover:bg-pink-700" 
                disabled={!maForm.name || !maForm.phone || (!editingMA && !maForm.password)}
                data-testid="save-ma-button"
              >
                {editingMA ? 'Update' : 'Add Medical Assistant'}
              </Button>
              <Button variant="outline" onClick={() => {
                setShowMADialog(false);
                setEditingMA(null);
              }} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Doctor Dialog */}
      <Dialog open={showDoctorDialog} onOpenChange={(open) => {
        setShowDoctorDialog(open);
        if (!open) {
          // Reset all states when dialog closes
          setEditingDoctor(null);
          setSearchedDoctor(null);
          setIsExistingDoctor(false);
          setSelectedPhoto(null);
        }
      }}>
        <DialogContent className="sm:max-w-md z-50 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingDoctor ? 'Edit Doctor' : 'Add Doctor'}</DialogTitle>
            <DialogDescription>
              {editingDoctor ? 'Update doctor information' : 'Search for existing doctor or add a new doctor to the hospital'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Registration Number - Moved to Top with Search (only for new doctors) */}
            {!editingDoctor && (
              <div>
                <Label htmlFor="doctor_registered_number">Registered Number *</Label>
                <div className="flex gap-2">
                  <Input
                    id="doctor_registered_number"
                    value={doctorForm.registered_number}
                    onChange={(e) => {
                      setDoctorForm({...doctorForm, registered_number: e.target.value});
                      // Reset search state when user modifies registration number
                      if (searchedDoctor) {
                        setSearchedDoctor(null);
                        setIsExistingDoctor(false);
                      }
                    }}
                    placeholder="e.g., 12345"
                    required
                    disabled={isExistingDoctor}
                    className={isExistingDoctor ? 'bg-gray-100' : ''}
                  />
                  <Button 
                    type="button" 
                    onClick={handleSearchDoctor}
                    disabled={searchingDoctor || !doctorForm.registered_number || isExistingDoctor}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {searchingDoctor ? 'Searching...' : 'Search'}
                  </Button>
                </div>
              </div>
            )}

            {/* Show search result card if doctor found */}
            {searchedDoctor && !editingDoctor && (
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <User className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-green-900">{searchedDoctor.name}</h4>
                      <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded">Existing Doctor</span>
                    </div>
                    <p className="text-sm text-green-700">{searchedDoctor.specialization}</p>
                    <p className="text-xs text-green-600 mt-1">Doctor ID: {searchedDoctor.id}</p>
                    {searchedDoctor.hospital_ids && searchedDoctor.hospital_ids.length > 0 && (
                      <p className="text-xs text-green-600 mt-1">
                        Currently working at {searchedDoctor.hospital_ids.length} hospital(s)
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {editingDoctor && (
              <div>
                <Label htmlFor="doctor_registered_number_edit">Registered Number</Label>
                <Input
                  id="doctor_registered_number_edit"
                  value={doctorForm.registered_number}
                  disabled
                  className="bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">Registered number cannot be changed</p>
              </div>
            )}

            <div>
              <Label htmlFor="doctor_name">Name *</Label>
              <Input
                id="doctor_name"
                value={doctorForm.name}
                onChange={(e) => setDoctorForm({...doctorForm, name: e.target.value})}
                placeholder="e.g., Jane Smith"
                required
                readOnly={isExistingDoctor}
                disabled={isExistingDoctor}
                className={isExistingDoctor ? 'bg-gray-100' : ''}
              />
            </div>
            <div>
              <Label htmlFor="doctor_specialization">Specialization *</Label>
              <Select
                value={doctorForm.specialization}
                onValueChange={(value) => setDoctorForm({...doctorForm, specialization: value, super_specialization: ''})}
                disabled={isExistingDoctor}
                required
              >
                <SelectTrigger className={isExistingDoctor ? 'bg-gray-100' : ''}>
                  <SelectValue placeholder="Select specialization" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="General Medicine">General Medicine</SelectItem>
                  <SelectItem value="Pediatrician">Pediatrician</SelectItem>
                  <SelectItem value="General Surgeon">General Surgeon</SelectItem>
                  <SelectItem value="Neurosurgeon">Neurosurgeon</SelectItem>
                  <SelectItem value="Cardiologist">Cardiologist</SelectItem>
                  <SelectItem value="Neurologist">Neurologist</SelectItem>
                  <SelectItem value="Pulmonologist">Pulmonologist</SelectItem>
                  <SelectItem value="Gastroenterologist">Gastroenterologist</SelectItem>
                  <SelectItem value="Endocrinologist">Endocrinologist</SelectItem>
                  <SelectItem value="Dermatologist">Dermatologist</SelectItem>
                  <SelectItem value="ENT Specialist">ENT Specialist</SelectItem>
                  <SelectItem value="Dentist">Dentist</SelectItem>
                  <SelectItem value="Psychiatrist">Psychiatrist</SelectItem>
                  <SelectItem value="Obstetrician/Gynec">Obstetrician/Gynec</SelectItem>
                  <SelectItem value="Oncologist">Oncologist</SelectItem>
                  <SelectItem value="Urologist">Urologist</SelectItem>
                  <SelectItem value="Physiotherapy">Physiotherapy</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Super Specialization - shown only if main specialization has options */}
            {doctorForm.specialization && SUPER_SPECIALIZATION_OPTIONS[doctorForm.specialization] && (
              <div>
                <Label htmlFor="doctor_super_specialization">Super Specialization</Label>
                <Select
                  value={doctorForm.super_specialization || "none"}
                  onValueChange={(value) => setDoctorForm({...doctorForm, super_specialization: value === "none" ? "" : value})}
                  disabled={isExistingDoctor}
                >
                  <SelectTrigger className={isExistingDoctor ? 'bg-gray-100' : ''}>
                    <SelectValue placeholder="Select super specialization (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {SUPER_SPECIALIZATION_OPTIONS[doctorForm.specialization].map((superSpec) => (
                      <SelectItem key={superSpec} value={superSpec}>{superSpec}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Optional: Select a sub-specialty within {doctorForm.specialization}</p>
              </div>
            )}
            <div>
              <Label htmlFor="doctor_contact">Contact *</Label>
              <PhoneInput
                value={doctorForm.contact}
                onChange={(value) => setDoctorForm({...doctorForm, contact: value})}
                countryCode={doctorForm.country_code}
                onCountryCodeChange={(code) => setDoctorForm({...doctorForm, country_code: code})}
                placeholder="Enter 10-digit mobile number"
                required
                disabled={isExistingDoctor}
                className={isExistingDoctor ? 'bg-gray-100' : ''}
              />
            </div>
            <div>
              <Label htmlFor="doctor_password">Password *</Label>
              <Input
                id="doctor_password"
                type="password"
                value={doctorForm.password}
                onChange={(e) => setDoctorForm({...doctorForm, password: e.target.value})}
                placeholder="Enter password for doctor login"
                required
                disabled={isExistingDoctor}
                className={isExistingDoctor ? 'bg-gray-100' : ''}
              />
            </div>
            <div>
              <Label htmlFor="doctor_schedule">Working Hours</Label>
              <Input
                id="doctor_schedule"
                value={doctorForm.schedule}
                onChange={(e) => setDoctorForm({...doctorForm, schedule: e.target.value})}
                placeholder="e.g., 09:00 AM - 05:00 PM"
              />
              <p className="text-xs text-gray-500 mt-1">Format: HH:MM AM/PM - HH:MM AM/PM</p>
            </div>
            <div>
              <Label htmlFor="doctor_photo">Photo</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  id="doctor_photo"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setSelectedPhoto(e.target.files[0]);
                    }
                  }}
                  className="hidden"
                />
                <label htmlFor="doctor_photo" className="cursor-pointer">
                  <div className="flex flex-col items-center">
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Click to upload photo</p>
                    <p className="text-xs text-gray-500 mt-1">JPG, PNG (Max 5MB)</p>
                  </div>
                </label>
                {selectedPhoto && (
                  <p className="text-sm text-green-600 mt-2 text-center">
                    ✓ {selectedPhoto.name}
                  </p>
                )}
                {doctorForm.photo_url && !selectedPhoto && (
                  <p className="text-sm text-gray-600 mt-2 text-center">
                    Current photo: {doctorForm.photo_url.split('/').pop()}
                  </p>
                )}
              </div>
            </div>
            {!editingDoctor && doctorForm.registered_number && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-800">
                <p className="font-medium">Doctor ID will be:</p>
                <p className="font-mono mt-1">
                  DOC{doctorForm.registered_number}
                </p>
              </div>
            )}
            <div className="flex space-x-2">
              <Button 
                onClick={handleSaveDoctor} 
                className="flex-1" 
                disabled={!doctorForm.name || !doctorForm.specialization || !doctorForm.contact || (!editingDoctor && !doctorForm.registered_number) || uploadingPhoto}
              >
                {uploadingPhoto ? 'Uploading...' : editingDoctor ? 'Update' : 'Create'}
              </Button>
              <Button variant="outline" onClick={() => {
                setShowDoctorDialog(false);
                setEditingDoctor(null);
                setSelectedPhoto(null);
              }} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
